import type {
  CoverageType,
  PremiumBlock,
} from "@/lib/submissions/coverageTerms";

export const COVERAGE_LABELS: Record<CoverageType, string> = {
  gl: "General liability",
  wc: "Workers’ comp",
  auto: "Commercial auto",
};
export interface CoverageDisplayPart {
  coverageType: string;
  label: string;
  marketType: "admitted" | "surplus_lines" | null;
  source?: string;
  premium: Record<string, unknown>;
  terms: Record<string, unknown>;
  fieldSources: Record<string, unknown>;
}
export interface CoverageRow {
  key: string;
  label: string;
  value: string;
  format: "money" | "date" | "text";
  qualifier?: string;
  source?: string;
}
const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const money = (value: unknown): value is string =>
  typeof value === "string" && /^-?\d+(?:\.\d{1,2})?$/.test(value);
const knownType = (value: string): value is CoverageType =>
  value === "gl" || value === "wc" || value === "auto";
function readPart(value: unknown): CoverageDisplayPart | null {
  const p = record(value);
  if (typeof p.coverageType !== "string") return null;
  return {
    coverageType: p.coverageType,
    label: knownType(p.coverageType)
      ? COVERAGE_LABELS[p.coverageType]
      : typeof p.label === "string"
        ? p.label
        : "Coverage",
    marketType:
      p.marketType === "admitted" || p.marketType === "surplus_lines"
        ? p.marketType
        : null,
    source: typeof p.source === "string" ? p.source : undefined,
    premium: record(p.premium),
    terms: record(p.terms),
    fieldSources: record(p.fieldSources),
  };
}
/** Unknown coverage types retain their named premium block, without guessed terms. */
export function coveragePartsOf(terms: unknown): CoverageDisplayPart[] {
  const raw = record(terms).coverageParts;
  if (!Array.isArray(raw)) return [];
  const order = ["gl", "wc", "auto"];
  return raw
    .flatMap((p: unknown) => {
      const read = readPart(p);
      return read ? [read] : [];
    })
    .sort(
      (a, b) =>
        (order.includes(a.coverageType) ? order.indexOf(a.coverageType) : 3) -
        (order.includes(b.coverageType) ? order.indexOf(b.coverageType) : 3),
    );
}
export function partEconomicsRows(part: unknown): CoverageRow[] {
  const p = record(record(part).premium);
  const rows: [keyof PremiumBlock, string][] = [
    ["grossPremium", "Gross premium"],
    ["agencyFee", "Agency fee"],
    ["taxes", "Taxes"],
    ["otherFees", "Other fees"],
    ["totalCost", "Total"],
  ];
  return rows.flatMap(([key, label]) =>
    money(p[key]) && (key !== "otherFees" || Number(p[key]) !== 0)
      ? [{ key, label, value: String(p[key]), format: "money" as const }]
      : [],
  );
}
export function termsTotalsOf(terms: unknown): CoverageRow[] {
  const totals = record(record(terms).totals);
  const rows = [
    ["totalPremium", "Total premium"],
    ["agencyFeeTotal", "Agency fees"],
    ["taxesTotal", "Taxes"],
    ["otherFeesTotal", "Other fees"],
    ["totalCost", "Total cost"],
  ];
  return rows.flatMap(([key, label]) =>
    money(totals[key]) &&
    (key !== "otherFeesTotal" || Number(totals[key]) !== 0)
      ? [{ key, label, value: String(totals[key]), format: "money" as const }]
      : [],
  );
}

type Definition = [string, string, "money" | "date" | "text" | "boolean"];
export const COVERAGE_ROW_DEFINITIONS: Record<CoverageType, Definition[]> = {
  gl: [
    ["eachOccurrence", "Each occurrence", "money"],
    ["generalAggregate", "General aggregate", "money"],
    [
      "productsCompletedOpsAggregate",
      "Products-completed operations aggregate",
      "money",
    ],
    ["personalAdvertisingInjury", "Personal & advertising injury", "money"],
    ["damageToRentedPremises", "Damage to premises rented to you", "money"],
    ["medicalExpense", "Medical expense (any one person)", "money"],
    ["deductibleAmount", "Deductible", "money"],
    ["policyTrigger", "Coverage trigger", "text"],
    ["retroactiveDate", "Retroactive date", "date"],
  ],
  wc: [
    ["partOneStatutory", "Workers’ compensation (Part One)", "boolean"],
    ["elEachAccident", "Employers liability — each accident", "money"],
    [
      "elDiseaseEachEmployee",
      "Employers liability — disease, each employee",
      "money",
    ],
    [
      "elDiseasePolicyLimit",
      "Employers liability — disease, policy limit",
      "money",
    ],
    ["deductibleAmount", "Deductible", "money"],
    ["uslhCoverage", "USL&H", "boolean"],
    ["voluntaryComp", "Voluntary compensation", "boolean"],
    ["foreignVoluntary", "Foreign voluntary compensation", "boolean"],
    ["governingState", "Governing state", "text"],
  ],
  auto: [
    ["combinedSingleLimit", "Combined single limit (each accident)", "money"],
    ["biPerPerson", "Bodily injury, per person", "money"],
    ["biPerAccident", "Bodily injury, per accident", "money"],
    ["pdPerAccident", "Property damage, per accident", "money"],
    ["umUimLimit", "Uninsured/underinsured motorist", "money"],
    ["medicalPayments", "Medical payments", "money"],
    ["comprehensiveDeductible", "Comprehensive deductible", "money"],
    ["collisionDeductible", "Collision deductible", "money"],
    ["hiredAuto", "Hired auto", "boolean"],
    ["nonOwnedAuto", "Non-owned auto", "boolean"],
  ],
};
const TEXT: Record<string, string> = {
  occurrence: "Occurrence",
  claims_made: "Claims made",
  property_damage: "property damage",
  bodily_injury: "bodily injury",
  per_claim: "per claim",
  per_occurrence: "per occurrence",
  small_deductible: "Small deductible",
  large_deductible: "Large deductible",
  retro: "Retrospective",
};
export function coverageRowsFor(input: unknown): CoverageRow[] {
  const p = readPart(input);
  if (!p || !knownType(p.coverageType)) return [];
  return COVERAGE_ROW_DEFINITIONS[p.coverageType].flatMap(
    ([key, label, format]): CoverageRow[] => {
      const raw = p.terms[key];
      if (raw === undefined || raw === null) return [];
      if (format === "money" && !money(raw)) return [];
      if (
        format === "boolean"
          ? typeof raw !== "boolean"
          : typeof raw !== "string"
      )
        return [];
      if (
        p.coverageType === "auto" &&
        key.startsWith("biPer") &&
        money(p.terms.combinedSingleLimit)
      )
        return [];
      if (
        p.coverageType === "auto" &&
        key === "pdPerAccident" &&
        money(p.terms.combinedSingleLimit)
      )
        return [];
      let qualifier: string | undefined;
      if (key === "generalAggregate") {
        const per = p.terms.generalAggregateAppliesPer;
        if (per === "policy" || per === "project" || per === "location")
          qualifier = `per ${per}`;
        else if (
          per === "other" &&
          typeof p.terms.generalAggregateAppliesPerOther === "string"
        )
          qualifier = p.terms.generalAggregateAppliesPerOther;
      }
      if (key === "deductibleAmount") {
        qualifier =
          [
            p.terms.deductibleAppliesTo,
            p.terms.deductibleBasis,
            p.terms.deductibleType,
          ]
            .flatMap((v) => (typeof v === "string" && TEXT[v] ? [TEXT[v]] : []))
            .join(", ") || undefined;
      }
      const value =
        format === "boolean"
          ? raw
            ? key === "partOneStatutory"
              ? "Statutory"
              : "Included"
            : "Excluded"
          : (TEXT[String(raw)] ?? String(raw));
      return [
        {
          key,
          label,
          value,
          format: format === "boolean" ? "text" : format,
          qualifier,
          source:
            typeof p.fieldSources[key] === "string"
              ? p.fieldSources[key]
              : undefined,
        },
      ];
    },
  );
}
export function taxesAndFeesFor(
  terms: unknown,
  coverageType: string | null,
): { label: string; amount: string }[] {
  const charges = record(terms).charges;
  if (!Array.isArray(charges)) return [];
  return charges.flatMap((raw: unknown) => {
    const c = record(raw);
    return c.coverageType === coverageType &&
      c.category !== "premium" &&
      typeof c.label === "string" &&
      money(c.amount)
      ? [{ label: c.label, amount: c.amount }]
      : [];
  });
}
export function policyTypeLabel(terms: unknown): string | null {
  const parts = coveragePartsOf(terms);
  if (!parts.length) return null;
  return `${record(terms).policyType === "package" || parts.length > 1 ? "Package policy · " : ""}${parts.map((p) => p.label).join(" + ")}`;
}
